import { ApolloLink, Observable, NextLink, Operation, FetchResult } from 'apollo-link';

interface PusherLinkOptions {
  pusher: any; // Replace with the appropriate Pusher type
}
class PusherLink extends ApolloLink {
  pusher: any;
  private activeChannels = new Map<string, { count: number; handler: any }>();

  constructor(options: PusherLinkOptions) {
    super();
    this.pusher = options.pusher;
  }

  request(operation: Operation, forward?: NextLink): Observable<FetchResult> | null {
    const subscribeObservable = new Observable<FetchResult>(() => {
      // no-op
    });

    const prevSubscribe = subscribeObservable.subscribe.bind(subscribeObservable);

    subscribeObservable.subscribe = (observerOrNext, onError?, onComplete?) => {
      prevSubscribe(observerOrNext, onError, onComplete);

      const observer = getObserver(observerOrNext, onError, onComplete);
      let subscriptionChannel: string | null = null;

      forward?.(operation)?.subscribe({
        next: (data: FetchResult) => {
          subscriptionChannel = data?.extensions?.lighthouse_subscriptions?.channel ?? null;

          if (!subscriptionChannel) {
            observer.next(data);
            observer.complete();
            return;
          }

          this.subscribeToChannel(subscriptionChannel, observer);
        },
        error: (err) => {
          observer.error?.(err);
        },
      });

      return {
        closed: false,
        unsubscribe: () => {
          if (subscriptionChannel) {
            this.unsubscribeFromChannel(subscriptionChannel);
          }
        },
      };
    };

    return subscribeObservable;
  }

  subscribeToChannel(subscriptionChannel: string, observer: any) {
    const existing = this.activeChannels.get(subscriptionChannel);

    if (existing) {
      existing.count += 1;
      return;
    }

    const handler = (payload: any) => {
      const result = payload?.result;

      if (result) {
        observer.next(result);
      }

      if (!payload?.more) {
        this.unsubscribeFromChannel(subscriptionChannel);
        observer.complete?.();
      }
    };

    this.pusher.subscribe(subscriptionChannel).bind('lighthouse-subscription', handler);

    this.activeChannels.set(subscriptionChannel, {
      count: 1,
      handler,
    });
  }

  unsubscribeFromChannel(subscriptionChannel: string) {
    const tracked = this.activeChannels.get(subscriptionChannel);

    if (!tracked) return;

    if (tracked.count > 1) {
      tracked.count -= 1;
      return;
    }

    const channel = this.pusher.channel(subscriptionChannel);
    if (channel) {
      channel.unbind('lighthouse-subscription', tracked.handler);
    }

    this.pusher.unsubscribe(subscriptionChannel);
    this.activeChannels.delete(subscriptionChannel);
  }
}

function getObserver(observerOrNext: any, onError?: any, onComplete?: any) {
  if (typeof observerOrNext === 'function') {
    return {
      next: (v: any) => observerOrNext(v),
      error: (e: any) => onError && onError(e),
      complete: () => onComplete && onComplete(),
    };
  } else {
    return {
      next: (v: any) => observerOrNext.next && observerOrNext.next(v),
      error: (e: any) => observerOrNext.error && observerOrNext.error(e),
      complete: () => observerOrNext.complete && observerOrNext.complete(),
    };
  }
}

export default PusherLink;
